import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, TextField, Divider, Alert, alpha } from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import AppleIcon from '@mui/icons-material/Apple';
import FacebookIcon from '@mui/icons-material/Facebook';
import { useAuth } from '@paneon/auth';
import { useTheme } from '@paneon/theme';
import { GlassCard, GlassContainer, GlowEffect, LoadingOverlay } from '@paneon/ui';

/**
 * Login page with social and email authentication
 */
export function LoginPage() {
  const navigate = useNavigate();
  const {
    signInWithGoogle,
    signInWithApple,
    signInWithFacebook,
    signInWithEmail,
    error,
    isLoading,
  } = useAuth();
  const { currentTheme, isDarkMode } = useTheme();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isEmailLogin, setIsEmailLogin] = useState(false);

  // Consistent input styles (matching dialogs)
  const inputStyles = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: isDarkMode ? alpha('#fff', 0.05) : alpha('#000', 0.03),
    },
    '& .MuiInputLabel-root': {
      color: isDarkMode ? alpha('#fff', 0.7) : alpha('#000', 0.6),
    },
  };

  const handleSocialLogin = async (provider: 'google' | 'apple' | 'facebook') => {
    try {
      if (provider === 'google') await signInWithGoogle();
      else if (provider === 'apple') await signInWithApple();
      else if (provider === 'facebook') await signInWithFacebook();
      navigate('/');
    } catch {
      // Error is handled by AuthContext
    }
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await signInWithEmail(email, password);
      navigate('/');
    } catch {
      // Error is handled by AuthContext
    }
  };

  return (
    <GlassContainer fullHeight>
      {/* Background glow effects */}
      <GlowEffect position="top-left" color={currentTheme.colors.primary} size={400} />
      <GlowEffect position="bottom-right" color={currentTheme.colors.secondary} size={300} />

      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* Logo and Title */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box
            component="img"
            src="/logo.png"
            alt="Paneon"
            sx={{
              width: 200,
              height: 'auto',
              mb: 2,
            }}
          />
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              background: currentTheme.gradient,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
              mb: 1,
            }}
          >
            Pets
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: isDarkMode ? alpha('#fff', 0.7) : alpha('#000', 0.7),
            }}
          >
            Verwalte die Ernährung deiner Haustiere
          </Typography>
        </Box>

        {/* Login Card */}
        <GlassCard sx={{ width: '100%', maxWidth: 400, p: 3 }}>
          <LoadingOverlay loading={isLoading}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {!isEmailLogin ? (
              <>
                {/* Social Login Buttons */}
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<GoogleIcon />}
                  onClick={() => handleSocialLogin('google')}
                  sx={{
                    mb: 2,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                  }}
                >
                  Mit Google anmelden
                </Button>

                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<AppleIcon />}
                  onClick={() => handleSocialLogin('apple')}
                  sx={{
                    mb: 2,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                  }}
                >
                  Mit Apple anmelden
                </Button>

                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<FacebookIcon />}
                  onClick={() => handleSocialLogin('facebook')}
                  sx={{
                    mb: 2,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                  }}
                >
                  Mit Facebook anmelden
                </Button>

                <Divider sx={{ my: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    oder
                  </Typography>
                </Divider>

                <Button
                  fullWidth
                  variant="text"
                  onClick={() => setIsEmailLogin(true)}
                  sx={{ textTransform: 'none' }}
                >
                  Mit E-Mail anmelden
                </Button>
              </>
            ) : (
              <>
                {/* Email Login Form */}
                <form onSubmit={handleEmailLogin}>
                  <TextField
                    fullWidth
                    label="E-Mail"
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    sx={{ ...inputStyles, mb: 2 }}
                  />

                  <TextField
                    fullWidth
                    label="Passwort"
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    sx={{ ...inputStyles, mb: 3 }}
                  />

                  <Button
                    fullWidth
                    type="submit"
                    variant="contained"
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: 'none',
                      mb: 2,
                    }}
                  >
                    Anmelden
                  </Button>
                </form>

                <Button
                  fullWidth
                  variant="text"
                  onClick={() => setIsEmailLogin(false)}
                  sx={{ textTransform: 'none' }}
                >
                  Zurück
                </Button>
              </>
            )}
          </LoadingOverlay>
        </GlassCard>

        {/* Footer */}
        <Typography
          variant="caption"
          sx={{
            mt: 4,
            color: isDarkMode ? alpha('#fff', 0.5) : alpha('#000', 0.5),
          }}
        >
          © 2025 Paneon / SSI
        </Typography>
      </Box>
    </GlassContainer>
  );
}
